<template>
    <section class="grid grid-cols-1 gap-[21px] sm:grid-cols-2 lg:grid-cols-4">
        <article
            v-for="card in cards"
            :key="card.label"
            class="rounded-[12.8px] border border-gray-200 bg-white p-6"
        >
            <div class="mb-4 flex items-start justify-between">
                <div
                    class="flex h-12 w-12 items-center justify-center rounded-[12.8px] bg-gray-100"
                >
                    <component :is="card.icon" class="h-6 w-6 text-gray-500" />
                </div>
            </div>
            <p class="mb-1 text-sm text-gray-600">{{ card.label }}</p>
            <p class="text-2xl font-semibold text-gray-900">{{ card.value }}</p>
            <p class="mt-1 text-[13px] leading-[18px] text-gray-500">
                {{ card.meta }}
            </p>
        </article>
    </section>
</template>

<script lang="ts" setup>
import { computed, type Component } from "vue";
import {
    ChartBarIcon,
    ExclamationTriangleIcon,
    GlobeAltIcon,
    ShieldCheckIcon,
} from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import type {
    IDmarcSetupOverview,
    IDomainPortfolio,
    IReportingOverview,
} from "@/types/Dashboard/IDashboardOverview";

interface SummaryCard {
    label: string;
    value: string;
    meta: string;
    icon: Component;
}

const props = defineProps<{
    domains: IDomainPortfolio;
    dmarc: IDmarcSetupOverview;
    reporting: IReportingOverview;
    attentionCount: number;
}>();

const { t } = useTranslations();

const cards = computed<SummaryCard[]>(() => [
    {
        label: t("vue.dashboard_overview_kpi_domains"),
        value: String(props.domains.total),
        meta: t("vue.dashboard_overview_kpi_domains_meta", {
            monitored: props.domains.monitored,
            parked: props.domains.parked,
            subdomains: props.domains.subdomains,
        }),
        icon: GlobeAltIcon,
    },
    {
        label: t("vue.dashboard_overview_kpi_dmarc"),
        value: `${props.dmarc.percentage}%`,
        meta: t("vue.dashboard_overview_kpi_dmarc_meta", {
            configured: props.dmarc.configured,
            monitored: props.dmarc.monitored,
        }),
        icon: ShieldCheckIcon,
    },
    {
        label: t("vue.dashboard_overview_kpi_reporting"),
        value: `${props.reporting.reporting} / ${props.reporting.monitored}`,
        meta: t("vue.dashboard_overview_kpi_reporting_meta"),
        icon: ChartBarIcon,
    },
    {
        label: t("vue.dashboard_overview_kpi_attention"),
        value: String(props.attentionCount),
        meta: t("vue.dashboard_overview_kpi_attention_meta"),
        icon: ExclamationTriangleIcon,
    },
]);
</script>
